import * as ReqType from '../types/ServerRequests';
import LoginServices from './LoginServices';
import Axios, { AxiosResponse } from 'axios';
import { AdresseService } from './AdresseService';
import { DisciplineService } from './DisciplineService';
import { NiveauService } from './NiveauService';
import { TagService } from './TagService';

/**
 * Cette classe permet la communication entre le backend et le frontend
 * Elle assure l'envois la reception des données entre le backend et frontend
 * Elle permet la creation, modification, et suppression des cours
 */
export class CoursService {
    private static instance: CoursService = new CoursService();

    public static getInstance(): CoursService {
        return this.instance;
    }

    /**
     * retourne la liste des cours
     * @returns {Promise<Course[]>}
     */
    public getCoursList(): Promise<ReqType.Course[]> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_COURSE_LIST_URL!,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Course[]>) => {
                return response.data;
            }
        );
    }

    /**
     * retourne un cours à partir d'un identifiant
     * @param {number} id
     * @returns {Promise<Course>}
     */
    public getCours(id: number): Promise<ReqType.Course> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_COURSE_VIEW_URL! + '&id=' + id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Course>) => {
                return response.data;
            }
        );
    }

    /**
     * crée un cours
     * @param {Course} newCours
     * @returns {Promise<Course>}
     */
    public createCours(newCours: ReqType.Course) {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Promise.all([
                AdresseService.getInstance().createAdresse(newCours.adresse),
            ]).then(
                (array) => {
                    var adresse: ReqType.Adresse = array[0];
                    var monCours = {
                        adresse_id: adresse.id,
                        details: newCours.details,
                        discipline_id: newCours.discipline.id,
                        jour: newCours.jour,
                        niveau_id: newCours.niveau.id,
                        nom_tag: newCours.tag.nom,
                        horaire: newCours.horaire,
                    };
                    return (Axios.post(
                        process.env.REACT_APP_COURSE_CREATE_URL!,
                        monCours,
                        {
                            headers: { 'X-Access-Token': token },
                        }
                    ));
                }
            ).then(
                (response) => {
                    // alert(JSON.stringify(response));
                    newCours.id = response.data.id;
                    return newCours;
                }
            )
        );
    }

    /**
     * modification et mise à jours d'un cours
     * @param {Course} newCours
     * @param {Course} oldCours
     * @returns {Promise<Course>}
     */
    public updateCours(newCours: ReqType.Course, oldCours: ReqType.Course) {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Promise.all([
                AdresseService.getInstance().createAdresse(newCours.adresse),
                TagService.getInstance().updateTag(newCours.tag),
            ]).then(
                (array) => {
                    var adresse: ReqType.Adresse = array[0];
                    var monCours = {
                        adresse_id: adresse.id,
                        details: newCours.details,
                        discipline_id: newCours.discipline.id,
                        jour: newCours.jour,
                        niveau_id: newCours.niveau.id,
                        status: newCours.status,
                        horaire: newCours.horaire,
                    };
                    return (Axios.put(
                        process.env.REACT_APP_COURSE_UPDATE_URL! + '&id=' + newCours.id,
                        monCours,
                        {
                            headers: { 'X-Access-Token': token },
                        }
                    ));
                }
            ).then(
                (response) => {
                    newCours.adresse = response.data.adresse;
                    return newCours;
                }
            )
        );
    }

    /**
     * retourne le cours par defaut
     * @returns {Course}
     */
    public getDefaultCourse(): ReqType.Course {
        var retVal: ReqType.Course = {
            adresse: AdresseService.getInstance().getDefaultAdresse(),
            details: '',
            discipline: DisciplineService.getInstance().getDefaultDiscipline(),
            id: -1,
            jour: '',
            horaire: '',
            niveau: NiveauService.getInstance().getDefaultNiveau(),
            status: 1,
            tag: {
                id: -1,
                nom: ''
            }
        };
        return (
            retVal
        );
    }

    /**
     * duplique un cours
     * @param {Course} origin
     * @returns {Course}
     */
    public duplicateCourse(origin: ReqType.Course): ReqType.Course {
        return JSON.parse(JSON.stringify(origin));
    }

    /**
     * generation des cours annuel
     * @param {Date} dd
     * @param {Date} df
     * @param {Date} hd
     * @param {Date} hf
     * @param {Course} cours
     * @returns {Promise<Course>}
     */
    public generateYearCourse(dd: Date, df: Date, hd: Date, hf: Date, cours: ReqType.Course) {
        const token = LoginServices.getInstance().getUserToken();

        while (dd.getDay() !== this.dayStringToNumber(cours.jour)) {
            dd.setDate(dd.getDate() + 1);
        }

        var timestampDebut = new Date(dd);
        var timestampFin = new Date(dd);

        timestampDebut.setHours(hd.getHours());
        timestampDebut.setMinutes(hd.getMinutes());

        timestampFin.setHours(hf.getHours());
        timestampFin.setMinutes(hf.getMinutes());

        return Axios.post(
            process.env.REACT_APP_COURSE_EVENT_GENERATE_URL!,
            {
                date_heure_debut: timestampDebut.valueOf() / 1000,
                date_heure_fin: timestampFin.valueOf() / 1000,
                date_fin: df.valueOf() / 1000,
                cours_id: cours.id
            },
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Course>) => {
                return response.data;
            }
        );
    }

    /**
     *
     * @param {string} s
     * @returns {number}
     */
    private dayStringToNumber(s: string): number {
        if (s.localeCompare('lundi') === 0) {
            return 1;
        } else if (s.localeCompare('mardi') === 0) {
            return 2;
        } else if (s.localeCompare('mercredi') === 0) {
            return 3;
        } else if (s.localeCompare('jeudi') === 0) {
            return 4;
        } else if (s.localeCompare('vendredi') === 0) {
            return 5;
        } else if (s.localeCompare('samedi') === 0) {
            return 6;
        } else {
            return 0;
        }
    }
}


// WEBPACK FOOTER //
// ./src/services/CoursService.tsx